import type { TUseAgCellRowDataRefresh } from 'components/AgGrid/types';
import type { WorkOrderPriceActValue, WorkOrderPriceItem } from 'gql/graphql';
import {
    ActKc2StatusEnum,
    ContractStatusEnum,
    DeviationEnum,
    WorkOrderPriceTypeEnum,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import { formatNumberToRu } from 'utils';
import { DeviatedCell } from 'utils/tableCells';

import type { ICellRendererParams } from 'ag-grid-community';
import { useCallback } from 'react';

import { WorkOrderPriceKC2VolumeEdit } from '../../WorkOrderPriceKC2VolumeEdit';

import { ActFormulaCellRenderer } from './ActFormulaCellRenderer';

export enum ActVolumeColType {
    planVolume = 'planVolume',
    acceptedVolume = 'acceptedVolume',
}
interface IActVolumeRenderer extends ICellRendererParams<WorkOrderPriceItem> {
    actId: number;
    seasonId: number;
    decimal: number;
    workOrderStatus: WorkOrderStatusEnum;
    contractStatus: ContractStatusEnum;
    deviationKey: keyof WorkOrderPriceActValue;
    valueKey: keyof WorkOrderPriceActValue;
    isDisabledByProjectStatus: boolean;
    colType: ActVolumeColType;
    useAgCellRowDataRefresh: TUseAgCellRowDataRefresh;
    useAgCellRowDataRefreshParams?: any;
    skzsPanelRefresh: () => void;
    refreshActs: () => void;
}

const requestParams = { isSMRItem: true };

export const ActVolumeRenderer = (props: IActVolumeRenderer) => {
    const {
        data,
        seasonId,
        workOrderStatus,
        actId,
        deviationKey,
        valueKey,
        contractStatus,
        isDisabledByProjectStatus,
        decimal = 3,
        colType,
        api,
        useAgCellRowDataRefresh,
        useAgCellRowDataRefreshParams,
        skzsPanelRefresh,
        refreshActs,
    } = props;

    const { reloadHandler } =
        useAgCellRowDataRefresh?.({
            api,
            requestParams: { ...useAgCellRowDataRefreshParams, ...requestParams },
        }) || {};

    const onChange = useCallback(
        (reloadRowsIds?: number[] | null) => {
            reloadHandler(reloadRowsIds);
            skzsPanelRefresh();
            refreshActs?.();
        },
        [refreshActs, reloadHandler, skzsPanelRefresh],
    );

    if (!data || data?.type === WorkOrderPriceTypeEnum.RootObject || data?.type === WorkOrderPriceTypeEnum.OtherGroup) {
        return '';
    }

    const { itemId, type, isExcludedPrice } = data ?? {};

    const isStatusWorkOrderDelayed = workOrderStatus === WorkOrderStatusEnum.Delayed;
    const isStatusWorkOrderCompleted = workOrderStatus === WorkOrderStatusEnum.Completed;

    const currentSeasonData = data?.seasonValues?.find((season) => season.seasonId === seasonId);
    const currentAct = currentSeasonData?.actValues?.find((act) => act.actId === actId);
    const actVolumeIsReadOnly = currentAct?.actVolumeIsReadOnly;
    const actStatus = currentAct?.actStatus;
    const value = formatNumberToRu(currentAct?.[valueKey] ?? '', decimal, 6);

    const isCoefficient = Boolean(
        type &&
            [
                WorkOrderPriceTypeEnum.ConstraintCoefficient,
                WorkOrderPriceTypeEnum.Coefficient,
                WorkOrderPriceTypeEnum.TechPartCoefficient,
                WorkOrderPriceTypeEnum.DismantlingCoefficient,
            ].includes(type),
    );

    const deviation = isCoefficient ? null : currentAct?.[deviationKey];

    const isDisabled = Boolean(
        isDisabledByProjectStatus ||
            isStatusWorkOrderDelayed ||
            isStatusWorkOrderCompleted ||
            isCoefficient ||
            contractStatus !== ContractStatusEnum.InImplementation ||
            actVolumeIsReadOnly ||
            isExcludedPrice,
    );

    if (type === WorkOrderPriceTypeEnum.Other) {
        return value;
    }

    if (actStatus === ActKc2StatusEnum.InProgress && colType === ActVolumeColType.planVolume) {
        return (
            <ActFormulaCellRenderer
                defaultValue={value}
                priceId={data?.itemId}
                currentAct={currentAct}
                itemType={type}
                isDisabled={isDisabled}
                refreshDataHandler={onChange}
            >
                <WorkOrderPriceKC2VolumeEdit
                    deviation={deviation}
                    actKC2Id={actId}
                    workOrderPriceId={itemId}
                    defaultValue={value}
                    onChange={onChange}
                    actKC2Status={actStatus}
                    disabled={isDisabled}
                />
            </ActFormulaCellRenderer>
        );
    }

    if (actStatus === ActKc2StatusEnum.OnCheck && colType === ActVolumeColType.acceptedVolume) {
        return (
            <WorkOrderPriceKC2VolumeEdit
                deviation={deviation}
                actKC2Id={actId}
                workOrderPriceId={itemId}
                defaultValue={value}
                onChange={onChange}
                actKC2Status={actStatus}
                disabled={isDisabled}
            />
        );
    }

    return value && deviation === DeviationEnum.Up ? <DeviatedCell>{value}</DeviatedCell> : value;
};

ActVolumeRenderer.displayName = 'ActVolumeRenderer';
